import React from "react";
import clsx from "clsx";
import { Link, useHistory } from "react-router-dom";

import AssessmentContent from "../../../organisms/teacher/AssessmentContent";
import {
  PAGE_1,
  PAGE_2,
  PAGE_3,
  PAGE_4,
  PAGE_5,
  PAGE_6,
  PAGE_7,
  X_LABELS_JSX,
} from "../../../constants/assessment-content";
import { IAssessmentAvgScores, IClass } from "../../../state";
import {
  assessmentPath,
  assessmentByClassPath,
  assessmentByGradePath,
} from "../../../teacherStore";
import { ReactComponent as ArrowIcon } from "../../../assets/svg/caret_down.svg";
import { ReactComponent as ArrowLeftIcon } from "../../../assets/svg/arrow_left_icon.svg";
import BarChartTeacher, {
  IAssessmentScore,
} from "../../../atoms/BarChartTeacher";
import Spinner from "../../../atoms/Spinner";
import ButtonsSwitch from "../../../atoms/ButtonsSwitch";
import DropdownAssessment from "../../../atoms/DropdownAssessment";

import styles from "./AssessmentByGrade.module.scss";

export type Props = {
  calling?: boolean;
  currentClass: IClass;
  grades: number[];
  avgScoresData: IAssessmentAvgScores[] | null;
  firstAvgScoresData: IAssessmentAvgScores[] | null;
  previousAvgScoresData: IAssessmentAvgScores[] | null;
};

const pages: any = { PAGE_1, PAGE_2, PAGE_3, PAGE_4, PAGE_5, PAGE_6, PAGE_7 };

const options = [
  { value: 0, label: "初回・現在" },
  { value: 1, label: "前回・現在" },
];

const AssessmentByGrade: React.FC<Props> = ({
  calling,
  currentClass,
  grades,
  avgScoresData,
  previousAvgScoresData,
  firstAvgScoresData,
}) => {
  const history = useHistory();
  const [tabActive, setTabActive] = React.useState(1);
  const [selectedTerms, setSelectedTerms] = React.useState<number[]>([]);
  const [
    currentChartData,
    setCurrentChartData,
  ] = React.useState<IAssessmentScore | null>(null);
  const [pastStudentChartData, setPastStudentChartData] = React.useState<
    IAssessmentScore[]
  >([]);
  const [selectedDropDown, setSelectedDropdown] = React.useState(
    options[0].value,
  );
  const currentIndex = React.useMemo(
    () => grades.findIndex(grade => grade === currentClass.grade),
    [currentClass.grade, grades],
  );

  const handleClickPrevClass = React.useCallback(() => {
    setSelectedTerms([]);
    history.push({
      search: `?grade=${
        currentIndex === 0 ? grades.slice(-1)[0] : grades[currentIndex - 1]
      }`,
    });
  }, [grades, history, currentIndex]);

  const handleClickNextClass = React.useCallback(() => {
    setSelectedTerms([]);
    history.push({
      search: `?grade=${
        currentIndex === grades.length - 1
          ? grades[0]
          : grades[currentIndex + 1]
      }`,
    });
  }, [grades, history, currentIndex]);

  React.useEffect(() => {
    if (avgScoresData) {
      const pastStudentData: IAssessmentScore[] = [];
      const studentScoresCurrent: number[] = avgScoresData
        ? avgScoresData.map(item => item.student_score ?? 0)
        : [];

      const selectedPastScores: IAssessmentAvgScores[] | null = selectedDropDown
        ? previousAvgScoresData
        : firstAvgScoresData;

      const studentScores: number[] = selectedPastScores
        ? selectedPastScores.map(item => item.student_score ?? 0)
        : [0, 0, 0, 0, 0, 0, 0];
      pastStudentData.push({ term_no: 0, scores: studentScores });

      setCurrentChartData({
        term_no: 1,
        scores: studentScoresCurrent,
      });
      setPastStudentChartData([...pastStudentData]);
    } else {
      setCurrentChartData({
        term_no: 0,
        scores: [0, 0, 0, 0, 0, 0, 0],
      });
      setPastStudentChartData([]);
    }
  }, [
    selectedTerms,
    avgScoresData,
    selectedDropDown,
    previousAvgScoresData,
    firstAvgScoresData,
  ]);

  const avgScore = React.useMemo(() => {
    if (!avgScoresData) {
      return null;
    }

    const { experience_no, student_score } = avgScoresData[tabActive - 1];

    return {
      experience_no,
      score: student_score,
      reasons: [],
    };
  }, [avgScoresData, tabActive]);

  return (
    <div className={clsx(styles.wrapper)}>
      <div className={styles.topContent}>
        <Link to={`${assessmentPath}?class_id=${currentClass.id}`}>
          <ArrowIcon /> 生徒・クラス一覧へ戻る
        </Link>
        <ButtonsSwitch
          buttonsData={[
            { content: "個人", path: assessmentPath },
            {
              content: "クラス集計",
              path: `${assessmentByClassPath}?class_id=${currentClass.id}`,
            },
            {
              content: "学年集計",
              path: `${assessmentByGradePath}?grade=${currentClass.grade}`,
            },
          ]}
        />
      </div>
      <div className={styles.contentContainer}>
        <div className={styles.contentWrapper}>
          {calling && <Spinner className={styles.loading} />}
          <div className={styles.headerTitle}>
            {grades.length > 1 && (
              <button
                className={styles.arrowLeft}
                onClick={handleClickPrevClass}
              >
                <ArrowLeftIcon />
              </button>
            )}
            <span>{currentClass.grade}年生</span>
            {grades.length > 1 && (
              <button
                className={styles.arrowRight}
                onClick={handleClickNextClass}
              >
                <ArrowLeftIcon />
              </button>
            )}
          </div>
          <div className="position-relative">
            {avgScoresData && (
              <DropdownAssessment
                options={options}
                defaultValue={options[0]}
                className={styles.dropTerm}
                onChange={val => {
                  setSelectedDropdown(val);
                }}
              />
            )}
            <div className={styles.summary}>
              {currentChartData && (
                <BarChartTeacher
                  currentData={currentChartData}
                  pastData={pastStudentChartData}
                />
              )}
            </div>
          </div>
          <div className={styles.switch}>
            {X_LABELS_JSX.map((label, index: number) => (
              <div
                key={index}
                className={clsx(
                  styles.switch__item,
                  tabActive === pages[`PAGE_${index + 1}`].id && [
                    styles.active,
                    styles.selected,
                  ],
                  avgScoresData &&
                    !!avgScoresData[index] &&
                    !!avgScoresData[index].student_score &&
                    styles.active,
                )}
                onClick={() => setTabActive(index + 1)}
              >
                <div>
                  <p>{index + 1}</p>
                  <span>{label}</span>
                </div>
              </div>
            ))}
          </div>
          <AssessmentContent
            content={pages[`PAGE_${tabActive}`]}
            assessmentExperience={avgScore}
            hasComment={false}
          />
        </div>
      </div>
    </div>
  );
};

export default AssessmentByGrade;
